
import Vue from 'vue';
import { mapState, mapActions, mapGetters } from 'vuex';
import { Runnit, RunnitMember, RUNNITS_ACCESS_LEVEL, RunnitsAccessLevel, TeamMember, RUNNIT_TYPE } from '@run-diffusion/shared';
import { get$bindFirestoreOptions } from '@/mixins';
import { db, functions, Query, QuerySnapshot } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { TeamUserMixin } from '@/mixins';
import _mapKeys from 'lodash/mapKeys';
import _omit from 'lodash/omit';
import _uniq from 'lodash/uniq';
import _uniqBy from 'lodash/uniqBy';
import {
	AccessLevelChoice,
	AccessLevelOption,
	bulkEditableRunnits,
	bulkSavePayload,
	hasAccessChanges,
	KEEP_ACCESS_LEVEL,
	ManageAccessPayload,
	ownerLabel,
	singleSavePayload,
} from '@/views/Runnits/RunnitManageAccessDialog/runnitOwnership';
import RunnitBoardMembersManagement from '@/views/Runnits/RunnitManageAccessDialog/RunnitBoardMembersManagement.vue';
import BulkModeRunnitBoardMembersManagement from '@/views/Runnits/RunnitManageAccessDialog/BulkModeRunnitBoardMembersManagement.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import DialogContent from '@/components/base/DialogContent.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import TeamMembersPicker from '@/components/TeamMembersPicker.vue';
import AddRunnitBoardMember from '@/views/Runnits/RunnitManageAccessDialog/AddRunnitBoardMember.vue';
import RunnitAccessLevelOptions from '@/views/Runnits/RunnitManageAccessDialog/RunnitAccessLevelOptions.vue';
import pluralize from 'pluralize';

export default Vue.extend({
	name: 'RunnitManageAccessDialog',
	mixins: [
		TeamUserMixin,
	],
	props: {
		value: { type: Boolean, default: false }, // For external control
		currRunnits: { type: Array, default: () => [] }, // For bulk mode
	},
	data () {
		const DEFAULT_BULK_PAGINATION = {
			sortBy: ['title'],
			sortDesc: [false],
		};
		return {
			RUNNITS_ACCESS_LEVEL,
			runnitMembers: [] as RunnitMember[],
			loadingRunnitMembers: false,
			loadingOwnerTeamMember: false,
			accessLevel: RUNNITS_ACCESS_LEVEL.PRIVATE as AccessLevelChoice,
			isAccessLevelDirty: false,
			saving: false,
			addedTeamMembers: [],
			removedMemberIds: {} as Record<string, true>,

			// The owner the board opened with (single mode), with its doc id so the picker marks it
			ownerTeamMember: null as TeamMember | null,
			// A new owner picked for the board (single mode); null keeps the one it has
			selectedOwner: null as TeamMember | null,
			// A new owner picked for every selected board (bulk mode); null keeps each board's own
			bulkNewOwner: null as TeamMember | null,
			// The selected boards' owners' emails, by user id (bulk mode)
			bulkOwnerEmails: {} as Record<string, string>,
			loadingBulkOwnerEmails: false,
			bulkOwnerEmailsRequest: 0,

			boardUpdateStatus: {} as Record<string, { loading: boolean; success?: boolean }>,
			bulkPagination: DEFAULT_BULK_PAGINATION,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
		]),
		...mapGetters([
			'displayLabels',
		]),
		isBulkMode (): boolean {
			return !!(this.value && this.currRunnits && this.currRunnits.length > 0);
		},
		isOpen (): boolean {
			return this.isBulkMode ? this.value : this.runnitState.runnitManageAccessOpen;
		},
		currentRunnits (): Runnit[] {
			if (this.isBulkMode) {
				return this.currRunnits as Runnit[];
			}
			return this.runnitState.runnit ? [this.runnitState.runnit] : [];
		},
		firstRunnit (): Runnit | null {
			return this.currentRunnits[0] || null;
		},
		dialogTitle (): string {
			if (this.isBulkMode) {
				const editableCount: number = this.editableRunnits.length;
				const isTemplate = this.currentRunnits.some((r: Runnit) => r.type === RUNNIT_TYPE.TEMPLATE);
				const itemType = isTemplate ? 'Template' : 'Board';
				return `Update Access Settings for ${editableCount} ${pluralize(itemType, editableCount)}`;
			}
			const itemType = this.firstRunnit?.type === RUNNIT_TYPE.TEMPLATE ? 'Template' : 'Board';
			return `Access Settings for '${this._get(this.firstRunnit, 'title') || `${itemType}`}'`;
		},
		bulkIntroText (): string {
			const count: number = this.editableRunnits.length;
			const boards: string = `${pluralize('board', count)}`;
			return this.showOwnerSection
				? `Choose a new owner, an access level, or both, for the ${count} selected ${boards} listed below:`
				: `Select an access level to apply to ${count} selected ${boards} listed below:`;
		},
		/** The team whose members can own these boards: the one in hand, in team mode only. */
		ownerTeamId (): string | null {
			return this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM
				? (this._get(this.team, 'id') || null)
				: null;
		},
		/** Whether the Owner field shows: to a holder of MANAGE_ALL_GENERATIONS, for a board it can move. */
		showOwnerSection (): boolean {
			if (this.isBulkMode) {
				return this.currentRunnits.some((runnit: Runnit) => this.canChangeRunnitOwner(runnit));
			}
			return this.canChangeRunnitOwner(this.firstRunnit);
		},
		isOwnerDirty (): boolean {
			if (this.isBulkMode) return !!this.bulkNewOwner;
			return !!(this.selectedOwner && this.firstRunnit && this.selectedOwner.userId !== this.firstRunnit.userId);
		},
		/** Who will own the board once saved (single mode). Null in bulk, where each board has its own. */
		effectiveOwnerUserId (): string | null {
			if (this.isBulkMode) return null;
			return this.isOwnerDirty ? this.selectedOwner.userId : (this._get(this.firstRunnit, 'userId') || null);
		},
		effectiveOwnerTeamMember (): TeamMember | null {
			if (this.isBulkMode) return null;
			return this.isOwnerDirty ? this.selectedOwner : this.ownerTeamMember;
		},
		ownerPickerSelected (): TeamMember | null {
			return this.isBulkMode ? this.bulkNewOwner : this.effectiveOwnerTeamMember;
		},
		/** The owner field's avatar letter, from the owner's email. None while each board keeps its own. */
		ownerInitial (): string {
			const email: string | null = this.isBulkMode
				? this._get(this.bulkNewOwner, 'email')
				: this._get(this.effectiveOwnerTeamMember, 'email');
			return email ? email.charAt(0).toUpperCase() : '';
		},
		ownerName (): string {
			if (this.isBulkMode) return this._get(this.bulkNewOwner, 'email') || 'Keep each board\'s current owner';
			if (!this.isOwnerDirty && this.loadingOwnerTeamMember) return 'Loading...';
			if (this.isEffectiveOwnerMe) return 'You';
			return this._get(this.effectiveOwnerTeamMember, 'email') || 'No owner';
		},
		ownerNameIsPlaceholder (): boolean {
			if (this.isBulkMode) return !this.bulkNewOwner;
			return !this.isOwnerDirty && this.loadingOwnerTeamMember;
		},
		/** Under the owner's name: their email when it says You, and what a pending change replaces. */
		ownerCaption (): string {
			if (this.isBulkMode) {
				if (!this.bulkNewOwner) return '';
				const count: number = this.editableRunnits.length;
				return `Will own the ${count} selected ${pluralize('board', count)}`;
			}
			if (this.isOwnerDirty) return `Changing from ${this.originalOwnerText}`;
			if (this.isEffectiveOwnerMe) return this._get(this.effectiveOwnerTeamMember, 'email') || '';
			return '';
		},
		originalOwnerText (): string {
			return ownerLabel(
				this._get(this.firstRunnit, 'userId') || null,
				this._get(this.ownerTeamMember, 'email') || null,
				this._get(this.user, 'id') || null,
			);
		},
		isEffectiveOwnerMe (): boolean {
			return !this.isBulkMode && !!this.user && this.effectiveOwnerUserId === this.user.id;
		},
		/** Whether the board belongs to the team in hand, which is what Restricted and Shared need. */
		showsTeamAccessLevels (): boolean {
			return !!(
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
				this.team &&
				this._get(this.firstRunnit, 'teamId')
			);
		},
		/**
		 * The "Who can access" cards. A level the caller cannot set is shown dimmed, and
		 * Restricted and Shared only on a team board, and only to someone who can set them
		 * or when the board already has them.
		 */
		accessLevelOptions (): AccessLevelOption[] {
			const options: AccessLevelOption[] = [];
			if (this.isBulkMode) {
				options.push({
					value: KEEP_ACCESS_LEVEL,
					label: 'Keep current',
					description: 'Each board keeps its own access level',
					icon: 'mdi-minus-circle-outline',
					color: 'var(--grey-300)',
				});
			}
			options.push({
				value: RUNNITS_ACCESS_LEVEL.PRIVATE,
				label: 'Private',
				description: `Only ${this.isEffectiveOwnerMe ? 'you' : 'the owner'} can access`,
				icon: 'mdi-lock-outline',
				color: 'var(--primary-pink)',
				disabled: !this.isPrivateEditable,
			});
			if (this.showsTeamAccessLevels && (this.isRestrictedEditable || this.accessLevel === RUNNITS_ACCESS_LEVEL.RESTRICTED)) {
				options.push({
					value: RUNNITS_ACCESS_LEVEL.RESTRICTED,
					label: 'Restricted',
					description: 'Specific members can access',
					icon: 'mdi-account-multiple-check-outline',
					color: 'var(--primary-blue)',
					disabled: !this.isRestrictedEditable,
				});
			}
			if (this.showsTeamAccessLevels && (this.isSharedEditable || this.accessLevel === RUNNITS_ACCESS_LEVEL.SHARED)) {
				options.push({
					value: RUNNITS_ACCESS_LEVEL.SHARED,
					label: 'Shared',
					description: `Everyone on the ${this.displayLabels.team} can access`,
					icon: 'mdi-account-group-outline',
					color: 'var(--primary-blue)',
					disabled: !this.isSharedEditable,
				});
			}
			return options;
		},
		/** Whether a single board's save changes its access, the owner aside. */
		singleHasAccessChanges (): boolean {
			if (this.isBulkMode || !this.firstRunnit) return false;
			return hasAccessChanges({
				runnit: this.firstRunnit,
				accessLevel: this.accessLevel as RunnitsAccessLevel,
				runnitMembers: this.runnitMembers,
				addedTeamMembers: this.addedTeamMembers,
				removedMemberIds: this.removedMemberIds,
				ownerUserId: this.effectiveOwnerUserId,
			});
		},
		/** The boards a save touches: every requested change applies to each, or the board is skipped. */
		editableRunnits (): Runnit[] {
			if (!this.isBulkMode) return this.currentRunnits;
			return bulkEditableRunnits(this.currentRunnits, {
				ownerRequested: !!this.bulkNewOwner,
				accessRequested: this.accessLevel !== KEEP_ACCESS_LEVEL,
				canManageAccess: (runnit: Runnit) => this.canManageRunnitAccess(runnit),
				canChangeOwner: (runnit: Runnit) => this.canChangeRunnitOwner(runnit),
			});
		},
		editableRunnitIds (): Record<string, true> {
			return this.editableRunnits.reduce((ids: Record<string, true>, runnit: Runnit) => {
				ids[runnit.id] = true;
				return ids;
			}, {} as Record<string, true>);
		},
		/** The boards whose access the caller can manage, whatever this save changes. */
		accessManageableRunnits (): Runnit[] {
			if (!this.isBulkMode) return this.currentRunnits;
			return this.currentRunnits.filter((runnit: Runnit) => this.canManageRunnitAccess(runnit));
		},
		isUserAdmin (): boolean {
			return !!(this.user && this.user.isAdmin);
		},
		featuredTemplatesCount (): number {
			if (!this.isBulkMode) return 0;
			return this.currentRunnits.filter((runnit: Runnit) =>
				runnit.type === RUNNIT_TYPE.TEMPLATE && runnit.isFeatured && !this.isUserAdmin
			).length;
		},
		nonEditableNonFeaturedCount (): number {
			if (!this.isBulkMode) return 0;
			return this.currentRunnits.filter((runnit: Runnit) =>
				!this.editableRunnitIds[runnit.id] && !(runnit.type === RUNNIT_TYPE.TEMPLATE && runnit.isFeatured)
			).length;
		},
		bulkHeaders () {
			return [
				{
					text: 'Board Name',
					value: 'title',
					align: 'left',
					class: 'py-0 pl-4 pr-1',
					sortable: true,
				},
				...(this.showOwnerSection ? [{
					text: 'Owner',
					value: 'owner',
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
					noDownload: true,
				}] : []),
				{
					text: 'Current Access Level',
					value: 'currentAccessLevel',
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
					noDownload: true,
				},
				{
					text: '',
					value: 'processing',
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
					noDownload: true,
				},
			];
		},
		/** Changes whenever the bulk table's owners need loading again. Empty when they do not show. */
		bulkOwnerEmailsKey (): string {
			if (!this.isBulkMode || !this.isOpen || !this.showOwnerSection) return '';
			return `${this.ownerTeamId}:${this.currentRunnits.map((runnit: Runnit) => `${runnit.id}/${runnit.userId}`).join(',')}`;
		},
		isRunnitOwnerOnSome (): boolean {
			return this.accessManageableRunnits.some((runnit: Runnit) => this.isRunnitOwnerCheck(runnit));
		},
		isPrivateEditable (): boolean {
			return !!(
				this.isRunnitOwnerOnSome || this.hasManageAllGenerationsPermission
			);
		},
		isRestrictedEditable (): boolean {
			return !!(
				this.hasShareRunnitAssetsPermission &&
				(this.isRunnitOwnerOnSome || this.hasManageAllGenerationsPermission)
			);
		},
		isSharedEditable (): boolean {
			return !!(
				this.hasShareRunnitAssetsPermission &&
				(this.isRunnitOwnerOnSome || this.hasManageAllGenerationsPermission)
			);
		},
		saveDisabled (): boolean {
			if (this.saving) return true;
			const needsSharePermission: boolean = (
				this.accessLevel !== RUNNITS_ACCESS_LEVEL.PRIVATE &&
				this.accessLevel !== KEEP_ACCESS_LEVEL
			);
			if (this.isBulkMode) {
				return !!(
					!this.editableRunnits.length ||
					(!this.isOwnerDirty && this.accessLevel === KEEP_ACCESS_LEVEL) ||
					(needsSharePermission && !this.hasShareRunnitAssetsPermission)
				);
			}
			// An owner change made alone sends no access level, so it needs no sharing permission
			const sendsAccess: boolean = !this.isOwnerDirty || this.singleHasAccessChanges;
			return !!(sendsAccess && needsSharePermission && !this.hasShareRunnitAssetsPermission);
		},
	},
	watch: {
		accessLevel: {
			immediate: false,
			handler (newVal: AccessLevelChoice, oldVal: AccessLevelChoice) {
				if (newVal !== oldVal && !this.isBulkMode) {
					this.isAccessLevelDirty = true;
				}
			},
		},
		value: {
			immediate: true,
			async handler (newVal: boolean) {
				if (newVal && this.isBulkMode) {
					this.accessLevel = KEEP_ACCESS_LEVEL;
					this.bulkNewOwner = null;
					this.boardUpdateStatus = {};
					this.addedTeamMembers = [];
				}
			},
		},
		bulkOwnerEmailsKey: {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal && newVal !== oldVal) this.loadBulkOwnerEmails();
			},
		},
		'runnitState.runnit': {
			immediate: true,
			handler (newVal: Runnit, oldVal: Runnit) {
				if (this.isBulkMode) return; // Skip in bulk mode

				const newRunnitId: string = newVal?.id || null;
				const oldRunnitId: string = oldVal?.id || null;
				if (newRunnitId && newRunnitId !== oldRunnitId) {
					this.loadRunnitMembers(newRunnitId, newVal?.teamId);
				}
				// Initialize access level from runnit
				if (newVal && newVal !== oldVal) {
					this.accessLevel = newVal.accessLevel || RUNNITS_ACCESS_LEVEL.PRIVATE;
				}
			},
		},
		'runnitState.runnitManageAccessOpen': {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (this.isBulkMode) return; // Skip in bulk mode

				if (newVal && newVal !== oldVal && this.runnitState.runnit?.id) {
					this.loadRunnitMembers(this.runnitState.runnit.id, this.runnitState.runnit.teamId);
				}
			},
		},
	},
	created () {
		this.resetFieldValues();
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'updateRunnitState',
		]),
		pluralize,
		isRunnitOwnerCheck (runnit: Runnit | null): boolean {
			if (!runnit) return false;
			return !!(
				this.user &&
				runnit.userId &&
				runnit.userId === this.user.id
			);
		},
		canManageRunnitAccess (runnit: Runnit): boolean {
			if (!runnit) return false;

			// For featured templates, only admins can manage access
			if (runnit.type === RUNNIT_TYPE.TEMPLATE && runnit.isFeatured) {
				return !!this.user?.isAdmin;
			}

			const isRunnitOwner: boolean = this.isRunnitOwnerCheck(runnit);

			return !!(
				(
					// Can switch it to PRIVATE without a permission if I'm the owner
					isRunnitOwner &&
					runnit.accessLevel !== RUNNITS_ACCESS_LEVEL.PRIVATE
				) ||
				(
					(isRunnitOwner || this.hasManageAllGenerationsPermission) &&
					this.hasShareRunnitAssetsPermission
				)
			);
		},
		isFeaturedTemplate (runnit: Runnit): boolean {
			return !!(
				runnit &&
				runnit.type === RUNNIT_TYPE.TEMPLATE &&
				runnit.isFeatured
			);
		},
		resetFieldValues () {
			this.selectedOwner = null;
			this.bulkNewOwner = null;
			if (this.isBulkMode) {
				this.accessLevel = KEEP_ACCESS_LEVEL;
				this.boardUpdateStatus = {};
				this.addedTeamMembers = [];
			} else {
				this.accessLevel = this.firstRunnit?.accessLevel || RUNNITS_ACCESS_LEVEL.PRIVATE;
				this.addedTeamMembers = [];
				this.removedMemberIds = {};
			}
		},
		onOwnerSelect (teamMember: TeamMember | null) {
			if (this.isBulkMode) {
				// Clearing the pick goes back to keeping each board's own owner
				this.bulkNewOwner = teamMember || null;
				return;
			}
			// Clearing the pick, or picking the board's own owner, keeps the owner it has
			if (!teamMember || teamMember.userId === this._get(this.firstRunnit, 'userId')) {
				this.selectedOwner = null;
				return;
			}
			this.selectedOwner = teamMember;
			// The owner is always on a RESTRICTED board, so removing them no longer stands
			if (this.removedMemberIds[teamMember.userId]) {
				this.removedMemberIds = _omit(this.removedMemberIds, teamMember.userId);
			}
		},
		onOwnerReset () {
			if (this.isBulkMode) this.bulkNewOwner = null;
			else this.selectedOwner = null;
		},
		/** A selected board's owner in the bulk table, whatever this save does to it. */
		bulkOwnerText (runnit: Runnit): string {
			const email: string | null = this.bulkOwnerEmails[runnit.userId] || null;
			const isMine: boolean = !!this.user && runnit.userId === this.user.id;
			if (!email && !isMine && this.loadingBulkOwnerEmails) return 'Loading...';
			return ownerLabel(runnit.userId || null, email, this._get(this.user, 'id') || null);
		},
		/** Whether this save gives the board a new owner. */
		willChangeOwner (runnit: Runnit): boolean {
			return !!(
				this.bulkNewOwner &&
				this.editableRunnitIds[runnit.id] &&
				runnit.userId !== this.bulkNewOwner.userId
			);
		},
		async loadBulkOwnerEmails () {
			const request: number = ++this.bulkOwnerEmailsRequest;
			const teamId: string | null = this.ownerTeamId;
			const ownerIds: string[] = _uniq(this.currentRunnits
				.filter((runnit: Runnit) => !!runnit.userId && runnit.teamId === teamId)
				.map((runnit: Runnit) => runnit.userId));
			if (!teamId || !ownerIds.length) {
				this.bulkOwnerEmails = {};
				return;
			}

			this.loadingBulkOwnerEmails = true;
			try {
				// One small query per owner: a selection rarely has more than a few of them
				const emails: Record<string, string> = {};
				await Promise.all(ownerIds.map(async (ownerId: string) => {
					const snapshot: QuerySnapshot = await db
						.collection(`teams/${teamId}/teamMembers`)
						.where('userId', '==', ownerId)
						.where('isAccepted', '==', true)
						.limit(1)
						.get();
					const email: string = this._get(snapshot.docs[0] && snapshot.docs[0].data(), 'email');
					if (email) emails[ownerId] = email;
				}));
				if (request === this.bulkOwnerEmailsRequest) this.bulkOwnerEmails = emails;
			} catch (error) {
				console.error('Error loading the selected boards\' owners:', error);
			} finally {
				if (request === this.bulkOwnerEmailsRequest) this.loadingBulkOwnerEmails = false;
			}
		},
		onRemoveExistingMembers (runnitMemberIds: string[]) {
			// Filter out owner to prevent removing them
			const ownerId: string | null = this.effectiveOwnerUserId;
			const filteredIds: string[] = runnitMemberIds.filter((id: string) => id !== ownerId);

			// Add to removedMemberIds map
			const newRemovedIds: Record<string, true> = { ...this.removedMemberIds };
			filteredIds.forEach((id: string) => {
				newRemovedIds[id] = true;
			});
			this.removedMemberIds = newRemovedIds;
		},
		onRemoveAddedMembers (teamMembers: TeamMember[]) {
			// Filter out owner to prevent removing them
			const ownerId: string | null = this.effectiveOwnerUserId;
			const filteredMembers: TeamMember[] = teamMembers.filter((tm: TeamMember) => tm.userId !== ownerId);

			// Remove from addedTeamMembers array
			const teamMemberIdsToRemove: Record<string, TeamMember> = _mapKeys(filteredMembers || [], 'id');
			const updatedMembers: TeamMember[] = this.addedTeamMembers.filter((m: TeamMember) => !teamMemberIdsToRemove[m.id]);
			this.addedTeamMembers = updatedMembers;
		},
		onAddedTeamMembersSelect (selectedMembers: TeamMember[]) {
			// Deduplicate: merge with existing addedTeamMembers and remove duplicates by userId
			const combined: TeamMember[] = [...this.addedTeamMembers, ...selectedMembers];
			const deduped: TeamMember[] = _uniqBy(combined, 'userId');
			this.addedTeamMembers = deduped;
		},
		setOpen (val: boolean, shouldReset: boolean) {
			if (this.isBulkMode) {
				// Bulk mode: emit to parent
				this.$emit('input', !!val);
				if (!val && shouldReset) {
					this.resetFieldValues();
				}
			} else {
				// Single mode: update vuex
				const oldVal: boolean = !!this.runnitState.runnitManageAccessOpen;
				this.updateRunnitState({
					runnitManageAccessOpen: !!val,
				});
				if (!val && val !== oldVal && shouldReset) {
					this.resetFieldValues();
				}
			}
		},
		onSaveClick () {
			// Do setTimeout, just in case I have the add members menu open, and I am clicking outside of it to close directly to the Save button
			setTimeout(async () => {
				if (this.isBulkMode) {
					this.onBulkSave();
				} else {
					this.onSingleSave();
				}
			}, 100);
		},
		/** One board's change, owner and access together. Throws unless it saved. */
		async saveAccessSettings (payload: ManageAccessPayload): Promise<void> {
			const functionRef = functions.httpsCallable('manageRunnitBoardAccessSettings');
			const { success, message } = ((await functionRef(payload)).data || {}) as { success?: boolean, message?: string };
			if (!success) {
				throw new Error(message || 'Failed to save access settings');
			}
		},
		async onSingleSave () {
			if (!this.firstRunnit || !this.accessLevel) return;

			const newOwnerUserId: string | null = this.isOwnerDirty ? this.selectedOwner.userId : null;
			const accessChanged: boolean = this.singleHasAccessChanges;
			try {
				this.saving = true;

				// Call backend function to handle everything
				await this.saveAccessSettings(singleSavePayload({
					runnitId: this.firstRunnit.id,
					accessLevel: this.accessLevel as RunnitsAccessLevel,
					newOwnerUserId,
					accessChanged,
					addedUserIds: this.addedTeamMembers.map((tm: TeamMember) => tm.userId),
					removedUserIds: Object.keys(this.removedMemberIds),
				}));

				this.updateSnackbar({
					status: SNACKBAR_STATUS.SUCCESS,
					message: newOwnerUserId && !accessChanged ? 'Success! Owner changed' : 'Success! Access settings saved',
					show: true,
				});

				this.setOpen(false, false);
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error! Problem saving access settings, please try again.',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
		async onBulkSave () {
			const runnits: Runnit[] = this.editableRunnits;
			if (!runnits || !runnits.length) return;
			const newOwnerUserId: string | null = this.bulkNewOwner ? this.bulkNewOwner.userId : null;
			const accessLevel: AccessLevelChoice = this.accessLevel;
			if (!newOwnerUserId && accessLevel === KEEP_ACCESS_LEVEL) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please choose a new owner or an access level',
					show: true,
				});
				return;
			}

			this.saving = true;

			// Initialize per-row update state to loading (only for editable boards)
			this.boardUpdateStatus = {};
			runnits.forEach((runnit: Runnit) => {
				this.$set(this.boardUpdateStatus, runnit.id, { loading: true });
			});

			const onError: Function = (e: any) => {
				console.error('Error updating Boards: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error updating board access settings',
					show: true,
				});
			};

			// In bulk mode, RESTRICTED replaces each board's entire member list. The backend
			// keeps each board's owner, and drops everyone else not listed.
			const setUserIds: string[] = this.addedTeamMembers.map((tm: TeamMember) => tm.userId);

			try {
				const updatePromises = runnits.map(async (runnit: Runnit) => {
					try {
						const payload: ManageAccessPayload | null = bulkSavePayload(runnit, { newOwnerUserId, accessLevel, setUserIds });
						// Nothing to send: the chosen owner already owns it, and its access level is kept
						if (payload) await this.saveAccessSettings(payload);
						this.$set(this.boardUpdateStatus, runnit.id, { loading: false, success: true });
						return { success: true, runnitId: runnit.id };
					} catch (err) {
						console.error('Error updating board', runnit?.title, err);
						this.$set(this.boardUpdateStatus, runnit.id, { loading: false, success: false });
						return { success: false, runnitId: runnit.id };
					}
				});

				const results: any[] = await Promise.all(updatePromises);
				const failedUpdates = results.filter(r => !r.success);

				if (failedUpdates.length > 0) {
					onError(new Error(`Failed to update ${failedUpdates.length} boards`));
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Success! Updated ${runnits.length} ${pluralize('board', runnits.length)}`,
						show: true,
					});
					this.$emit('success');
					this.setOpen(false, false);
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error updating boards, please try again',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
		async loadRunnitMembers (runnitId: string, teamId: string) {
			if (!runnitId) {
				this.runnitMembers = [];
				return;
			}

			// Load owner TeamMember
			this.loadingOwnerTeamMember = true;
			try {
				if (!teamId) {
					this.loadingOwnerTeamMember = false;
					return;
				}

				const ownerTeamMemberRef: Query = db
					.collection(`teams/${teamId}/teamMembers`)
					.where('userId', '==', this.runnitState.runnit.userId)
					.limit(1);
				const ownerTeamMemberSnapshot = await ownerTeamMemberRef.get();
				const ownerTeamMemberDoc = ownerTeamMemberSnapshot.docs?.[0];
				// With its doc id, which the owner picker keys its selection by
				const ownerTeamMember: TeamMember | null = ownerTeamMemberDoc
					? { ...ownerTeamMemberDoc.data(), id: ownerTeamMemberDoc.id } as TeamMember
					: null;
				this.ownerTeamMember = ownerTeamMember;
				if (ownerTeamMember) {
					this.onAddedTeamMembersSelect([ownerTeamMember]);
				}
			} catch (error) {
				console.error('Error loading runnit members:', error);
			} finally {
				this.loadingOwnerTeamMember = false;
			}

			// Load RunnitMembers
			this.loadingRunnitMembers = true;
			try {
				const runnitMembersRef: Query = db
					.collection(`runnits/${runnitId}/runnitMembers`)
					.limit(4000);

				await this.$bind(
					'runnitMembers',
					runnitMembersRef,
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error loading runnit members:', error);
			} finally {
				this.loadingRunnitMembers = false;
			}
		},
	},
	components: {
		RunnitBoardMembersManagement,
		BulkModeRunnitBoardMembersManagement,
		DialogOrBottomSheet,
		DialogContent,
		ActionsIsland,
		TeamMembersPicker,
		AddRunnitBoardMember,
		RunnitAccessLevelOptions,
	},
});
